// KST(UTC+9) 고정 날짜·시각 유틸 — 브라우저·서버 시간대와 무관하게 한국시간 기준.
// 서버(gift-channel-ledger 등)는 절대시각(instant)만 저장·비교하므로
// 하루 경계 계산과 표기를 KST로 고정하는 것은 전부 프론트 몫이다.
// 주의: Date의 로컬 getter(getHours 등)나 toISOString().slice()를 직접 쓰면
// 브라우저/UTC 기준이 섞여 날짜가 하루 밀린다 — 반드시 이 유틸을 거칠 것.

const KST_OFFSET_MS = 9 * 3600_000;

/** Date → KST 벽시계 기준 ISO 문자열 (내부용 — 반환값은 Z가 붙지만 KST 표기) */
const toKstISO = (d: Date) => new Date(d.getTime() + KST_OFFSET_MS).toISOString();

/** KST 기준 'YYYY-MM-DD' */
export const kstDateOf = (d: Date) => toKstISO(d).slice(0, 10);

/** KST 기준 오늘 'YYYY-MM-DD' */
export const kstToday = () => kstDateOf(new Date());

/** KST 기준 'HH:mm' */
export const kstTimeHHmm = (d: Date) => toKstISO(d).slice(11, 16);

/** KST 기준 시간 버킷 키 'YYYY-MM-DDTHH' — 1시간 단위 묶음용 */
export const kstHourKeyOf = (d: Date) => toKstISO(d).slice(0, 13);

/** KST 기준 'YYYY.MM.DD HH:mm' */
export const kstDateTimeLabel = (d: Date) => {
  const iso = toKstISO(d);
  return `${iso.slice(0, 10).replace(/-/g, '.')} ${iso.slice(11, 16)}`;
};

/** KST 기준 'M/D HH:mm' (앞자리 0 제거) */
export const kstShortDateTimeLabel = (d: Date) => {
  const [, m, day] = kstDateOf(d).split('-');
  return `${Number(m)}/${Number(day)} ${kstTimeHHmm(d)}`;
};

/** 'YYYY-MM-DD'를 KST 자정 기준으로 delta일 이동한 'YYYY-MM-DD' */
export const kstShiftDay = (date: string, delta: number) =>
  kstDateOf(new Date(new Date(`${date}T00:00:00+09:00`).getTime() + delta * 86_400_000));

/** 'YYYY-MM-DD' → KST 하루 시작 ISO (빈 값은 undefined) */
export const kstDayStartISO = (date: string) =>
  date ? new Date(`${date}T00:00:00+09:00`).toISOString() : undefined;

/** 'YYYY-MM-DD' → KST 하루 끝 ISO (빈 값은 undefined) */
export const kstDayEndISO = (date: string) =>
  date ? new Date(`${date}T23:59:59.999+09:00`).toISOString() : undefined;

/** 현재 시각의 KST datetime-local 값 'YYYY-MM-DDTHH:mm' */
export const kstDatetimeLocalNow = () => toKstISO(new Date()).slice(0, 16);

/** datetime-local 값('YYYY-MM-DDTHH:mm[:ss]')을 KST 시각으로 해석해 Date로 */
export const parseKstDatetimeLocal = (value: string) =>
  new Date(`${value.length === 16 ? `${value}:00` : value}+09:00`);

/** 'YYYY-MM-DD' 두 날짜 사이의 모든 날짜(양끝 포함). from > to면 빈 배열 */
export const kstDateRangeDays = (from: string, to: string): string[] => {
  const days: string[] = [];
  if (!from || !to || from > to) return days;
  for (let d = from; d <= to; d = kstShiftDay(d, 1)) {
    days.push(d);
    //* 잘못된 입력으로 무한 루프가 나지 않도록 상한 (약 5.5년)
    if (days.length > 2000) break;
  }
  return days;
};
